import isEmpty from 'lodash/isEmpty';
import { bool, func, number, string } from 'prop-types';
import React from 'react';
import { Col, Input, Label, Row } from 'reactstrap';

import CategorySelect from '../../generic/CategorySelect';
import CurrencyFormat from '../../generic/CurrencyFormat';
import DateRange from '../../generic/DateRange';
import MultiSelect from '../../generic/MultiSelect';
import SingleSelect from '../../generic/SingleSelect';
import InfoTooltip from '../../generic/Tooltip/InfoTooltip';
import { getOperativeSystem } from '../../products/constants';
import { campaignLineShape } from '../../prop-types';
import HELP_TEXTS from '../../quote-editor/utils/help-texts';
import { countryOptions, genders } from '../../quotes/constants';
import convertToOption from '../../utils/convert-to-option';
import FEATURE_FLAGS, { useSafeFlag } from '../../utils/flags';
import { targetings } from '../constants';
import { isEditable, isNotConfigurable, targetingOptions } from '../utils';
import BudgetDetailsPopover from './BudgetDetailsPopover';
import CampaignLineStatus from './CampaignLineStatus';

function getPricingOptions({ possiblePriceModels }) {
  return possiblePriceModels.map(convertToOption);
}

function LanguageIndependentCampaignLine({
  label,
  line,
  onChange,
  index,
  readOnlyGender,
}) {
  const isDraftCampaignEnabled = useSafeFlag(FEATURE_FLAGS.DRAFT_CAMPAIGN);

  if (isNotConfigurable(line)) {
    return 'This line can not be configured further.';
  }

  const { id, selected: isSelected } = line;
  const { product } = line;
  const { supportsProductTags, supportsAdManager } = product;
  const operativeSystem = supportsAdManager
    ? 'zms_am'
    : product.operativeSystem;
  const isZmsAm =
    operativeSystem === getOperativeSystem(isDraftCampaignEnabled).ccp.system;
  const isCampaignLineEditable = onChange && isEditable(line);
  const isReadOnly = !isCampaignLineEditable || isZmsAm;
  const inputProps = isDraftCampaignEnabled
    ? { onChange, readOnly: isReadOnly }
    : { onChange, readOnly: !isCampaignLineEditable };
  const selectCheckboxId = `${id}.selected`;
  const pricingOptions = getPricingOptions(product);

  const isExisting = id;
  const pathPrefix = id || index;
  const columnSizing = {
    date: isExisting ? 2 : 4,
    gender: isExisting ? 2 : 3,
    additionalProperties: isExisting ? 9 : 12,
  };
  return (
    <>
      <Row className="align-items-center">
        {isExisting && (
          <Col sm={3} className="d-flex align-items-baseline">
            {isDraftCampaignEnabled && !isZmsAm && (
              <input
                type="checkbox"
                id={selectCheckboxId}
                name={selectCheckboxId}
                checked={isSelected}
                disabled={inputProps.readOnly}
                className="mr-2"
                {...inputProps}
              />
            )}
            {isDraftCampaignEnabled && isZmsAm && (
              <InfoTooltip
                target={`disabled-ccp-campaign-tooltip-${id}`}
                className="mr-2 mt-2"
              >
                {HELP_TEXTS.tooltips.disabledCcpCampaign}
              </InfoTooltip>
            )}
            {!isDraftCampaignEnabled && (
              <input
                type="checkbox"
                id={selectCheckboxId}
                name={selectCheckboxId}
                checked={isSelected}
                disabled={inputProps.readOnly}
                className="mr-2"
                {...inputProps}
              />
            )}
            <Label for={selectCheckboxId} className="mr-2 mb-0 h6">
              {label}
            </Label>
            <Label for={selectCheckboxId} className="mb-0 small text-muted">
              {line.number}
            </Label>
            {supportsProductTags && (
              <div className="ml-3">
                <Input
                  size="10"
                  name={`${pathPrefix}.productTag`}
                  id={`${pathPrefix}.productTag`}
                  value={line.productTag}
                  {...inputProps}
                />
              </div>
            )}
          </Col>
        )}
        {isExisting && (
          <Col sm={2} className="d-flex align-items-center">
            <CurrencyFormat
              className="mr-2 c-campaign-line__value"
              value={line.spendableBudget}
              text
            />
            <BudgetDetailsPopover
              id={id}
              freeService={line.freeService}
              netClientBudget={line.netClientBudget}
              netClientBudgetExternal={line.netClientBudgetExternal}
              wkzBudget={line.wkzBudget}
              coInvest={line.coInvest}
              identifier="campaign-line"
            />
          </Col>
        )}
        <Col sm={columnSizing.date}>
          {product.overwriteTimeRange &&
            (!isDraftCampaignEnabled || line.startDate) && (
              <DateRange
                startName={`${pathPrefix}.startDate`}
                endName={`${pathPrefix}.endDate`}
                startDate={line.startDate}
                endDate={line.endDate}
                small
                className="d-inline-block"
                {...inputProps}
              />
            )}
        </Col>
        <Col sm={columnSizing.gender}>
          {product.overwriteGender && (
            <>
              <Label className="mb-0" for={`${pathPrefix}.gender`} hidden>
                Gender
              </Label>
              <SingleSelect
                options={genders}
                name={`${pathPrefix}.gender`}
                value={line.gender}
                placeholder="Gender"
                className="c-campaign-line-select d-inline-block"
                {...inputProps}
                readOnly={inputProps.readOnly || readOnlyGender}
              />
            </>
          )}
        </Col>
        <Col className="text-right">
          <CampaignLineStatus status={line.status} />
        </Col>
      </Row>
      <Row className="mt-3">
        {isExisting && <Col sm={3} />}
        <Col sm={columnSizing.additionalProperties}>
          <ul className="list-unstyled list-inline d-flex align-items-center m-0">
            <li className="list-inline-item">
              <Label className="mb-0" for={`${pathPrefix}.pricing`} hidden>
                Pricing
              </Label>
              {product.adjustedFee ? (
                <>
                  <Input
                    size="10"
                    name={`${pathPrefix}.adjustedFee`}
                    id={`${pathPrefix}.adjustedFee`}
                    value={line.adjustedFee}
                    {...inputProps}
                  />
                  <Label className="mb-0 small text-muted">Fee %</Label>
                </>
              ) : (
                <SingleSelect
                  styles={{ input: base => ({ ...base, width: 70 }) }}
                  options={pricingOptions}
                  name={`${pathPrefix}.pricing`}
                  id={`${pathPrefix}.pricing`}
                  value={line.pricing}
                  {...inputProps}
                  disabled={pricingOptions.length <= 1}
                />
              )}
            </li>
            {!isEmpty(line.secondaryCountryCodes) && (
              <li className="list-inline-item">
                <Label
                  className="mb-0"
                  for={`${pathPrefix}.secondaryCountryCodes`}
                  hidden
                >
                  Countries
                </Label>
                <MultiSelect
                  options={countryOptions}
                  name={`${pathPrefix}.secondaryCountryCodes`}
                  value={line.secondaryCountryCodes}
                  placeholder="Countries"
                  className="c-campaign-line-select"
                  {...inputProps}
                  readOnly
                />
              </li>
            )}
            {product.overwriteCategories && (
              <li className="list-inline-item">
                <Label className="mb-0" for={`${pathPrefix}.categories`} hidden>
                  Categories
                </Label>
                <CategorySelect
                  name={`${pathPrefix}.categories`}
                  value={line.categories}
                  className="c-campaign-line-select"
                  {...inputProps}
                />
              </li>
            )}
            {product.supportsTargeting && (
              <li className="list-inline-item">
                <Label className="mb-0" for={`${pathPrefix}.targeting`} hidden>
                  Targeting
                </Label>
                <SingleSelect
                  styles={{ input: base => ({ ...base, width: 70 }) }}
                  options={targetingOptions(product)}
                  name={`${pathPrefix}.targeting`}
                  id={`${pathPrefix}.targeting`}
                  value={line.targeting || targetings.NO_EXTENSION}
                  placeholder="Select Targeting..."
                  {...inputProps}
                />
              </li>
            )}
            {product.serviceDescription && (
              <li className="list-inline-item">
                <Label className="mb-0" for={`${pathPrefix}.desc1`} hidden>
                  Description
                </Label>
                <Input
                  bsSize="sm"
                  className="c-campaign-line-service-description-input"
                  name={`${pathPrefix}.desc1`}
                  id={`${pathPrefix}.desc1`}
                  value={line.desc1}
                  type="textarea"
                  {...inputProps}
                />
              </li>
            )}
          </ul>
        </Col>
      </Row>
    </>
  );
}

LanguageIndependentCampaignLine.propTypes = {
  label: string.isRequired,
  line: campaignLineShape.isRequired,
  onChange: func,
  index: number,
  readOnlyGender: bool,
};

LanguageIndependentCampaignLine.defaultProps = {
  onChange: undefined,
  index: null,
  readOnlyGender: false,
};

export default LanguageIndependentCampaignLine;
